import {
  Container,
  Divider,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { showNotification } from "@mantine/notifications"
import { Button, EqualWidthGrid } from "@tfg/ui"
import { QRCodeSVG } from "qrcode.react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { userUpdateProfileSchema } from "@tfg/types/schemas/user"

import {
  useHelloControllerGetHello,
  useHelloControllerBreak,
  useHelloControllerBreakWorkers,
} from "~/gen/api"
import { useAuth } from "~/store/auth-context"

import {
  PasswordForm,
  passwordSchema,
  ProfileForm,
  useConfigureTwoFactorMutation,
  useUpdatePasswordMutation,
  useUpdateProfileMutation,
} from "./hooks"

export const Profile = () => {
  const { t } = useTranslation(["account"])
  const { user, isAdmin, checkAuth } = useAuth()
  const [twoFactorUrl, setTwoFactorUrl] = useState<string>("")
  const [resetModalOpen, setResetModalOpen] = useState(false)
  const profileForm = useForm<ProfileForm>({
    validate: zodResolver(userUpdateProfileSchema),
    initialValues: {
      name: user?.name || "",
    },
  })

  const { mutate: getHelloData } = useHelloControllerGetHello()
  const { mutate: breakAppBack } = useHelloControllerBreak()
  const { mutate: breakWorkers } = useHelloControllerBreakWorkers()

  const passwordForm = useForm<PasswordForm>({
    validate: zodResolver(passwordSchema),
    initialValues: {
      password: "",
      confirm: "",
    },
  })

  const { mutateAsync: updateProfile, isLoading: isUpdatingProfile } =
    useUpdateProfileMutation({
      onSuccess: () => {
        checkAuth()
        showNotification({ message: t("profile.updateSuccess") })
      },
    })

  const { mutateAsync: updatePassword, isLoading: isUpdatingPassword } =
    useUpdatePasswordMutation({
      onSuccess: () => {
        checkAuth()
        showNotification({ message: t("profile.updateSuccess") })
      },
    })

  const { mutateAsync: generateTwoFactorToken } = useConfigureTwoFactorMutation(
    {
      onSuccess: (data) => {
        setTwoFactorUrl(data.url)
      },
    }
  )

  return (
    <Container fluid p={0}>
      <Stack pb="sm" bg="var(--mantine-color-brand-background)">
        <Stack px="lg" pt="md">
          <Title order={2}>{t("profile.title")}</Title>
        </Stack>
      </Stack>
      <Container fluid py="xl" px="lg">
        <Group align="flex-start">
          <form
            onSubmit={profileForm.onSubmit((values) => updateProfile(values))}
          >
            <Stack maw={400}>
              <Title order={3}>{t("profile.titles.info")}</Title>
              <TextInput
                label={t("profile.labels.name")}
                placeholder={t("profile.placeholders.name")}
                {...profileForm.getInputProps("name")}
              />
              <Tooltip label={t("profile.tooltips.email")} withArrow>
                <TextInput
                  type="email"
                  disabled={true}
                  size="sm"
                  label={t("profile.labels.email")}
                  value={user?.email || ""}
                />
              </Tooltip>
              <Button
                type="submit"
                variant="primary"
                loading={isUpdatingProfile}
                disabled={!profileForm.isValid()}
              >
                {t("profile.submit.infos")}
              </Button>
            </Stack>
          </form>
          <Divider orientation="vertical" h={300} color="gray.2" mx={50} />
          <form
            onSubmit={passwordForm.onSubmit((values) => updatePassword(values))}
          >
            <Stack maw={400}>
              <Title order={3}>{t("profile.titles.password")}</Title>
              <TextInput
                type="password"
                label={t("profile.labels.password")}
                placeholder={t("profile.placeholders.password")}
                {...passwordForm.getInputProps("password")}
              />
              <Text
                c="gray"
                size="xs"
                style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                mt={-12}
              >
                {t("profile.passwordHelp", { minLength: isAdmin ? 16 : 12 })}
              </Text>
              <TextInput
                type="password"
                label={t("profile.labels.confirm")}
                placeholder={t("profile.placeholders.confirm")}
                {...passwordForm.getInputProps("confirm")}
              />
              <Button
                type="submit"
                variant="primary"
                loading={isUpdatingPassword}
                disabled={!passwordForm.isValid()}
              >
                {t("profile.submit.password")}
              </Button>
            </Stack>
          </form>
          {isAdmin ? (
            <>
              <Divider orientation="vertical" h={300} color="gray.2" mx={50} />
              <Stack>
                {twoFactorUrl.length > 0 ? (
                  <QRCodeSVG value={twoFactorUrl} />
                ) : null}
                <Button onClick={() => setResetModalOpen(true)}>
                  Reset 2FA
                </Button>
                <Modal
                  opened={resetModalOpen}
                  onClose={() => setResetModalOpen(false)}
                  title="Confirm 2FA Reset"
                  centered
                >
                  <Stack>
                    <Text>{t("profile.2faModal.confirmation")}</Text>
                    <Text size="sm" c="dimmed">
                      {t("profile.2faModal.description")}
                    </Text>
                    <EqualWidthGrid>
                      <Button
                        variant="secondary-critical"
                        onClick={() => setResetModalOpen(false)}
                        fullWidth
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        fullWidth
                        onClick={() => {
                          generateTwoFactorToken()
                          setResetModalOpen(false)
                        }}
                      >
                        Reset 2FA
                      </Button>
                    </EqualWidthGrid>
                  </Stack>
                </Modal>
                <Button onClick={() => getHelloData(undefined as never)}>
                  Test Worker
                </Button>
                <Button
                  onClick={() => {
                    throw new Error("This is your new error!")
                  }}
                >
                  Break the front
                </Button>

                <Button onClick={() => breakAppBack(undefined as never)}>
                  Break the back
                </Button>

                <Button onClick={() => breakWorkers(undefined as never)}>
                  Break the workers
                </Button>
              </Stack>
            </>
          ) : null}
        </Group>
      </Container>
    </Container>
  )
}
